import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import get from 'lodash/get';
import Tab from '../../../components/Tab';
import './styles.css';

import TuitionAndFeesChart from './TuitionAndFeesChart';

const messages = defineMessages({
  title: {
    id: 'hubs.costs.tuition-and-fees.title',
    defaultMessage: 'Tuition and Fees',
  },
  subText: {
    id: 'hubs.costs.tuition-and-fees.subtext',
    defaultMessage:
      'Most colleges group tuition and fees together. Tuition is the cost of taking courses. Fees vary by institution but often cover shared resources, such as libraries and computer labs.',
  },
  totalCost: {
    id: 'hubs.costs.tuition-and-fees.total-cost',
    defaultMessage: 'Total Cost',
  },
  tuition: {
    id: 'hubs.costs.tuition-and-fees.tuition',
    defaultMessage: 'Tuition',
  },
  fees: {
    id: 'hubs.costs.tuition-and-fees.fees',
    defaultMessage: 'Fees',
  },
  roomAndBoard: {
    id: 'hubs.costs.tuition-and-fees.room-and-board',
    defaultMessage: 'Room and Board',
  },
  creditHourTuition: {
    id: 'hubs.costs.tuition-and-fees.credit-hour-tuition',
    defaultMessage: 'Tuition Per Credit Hour',
  },
});

class TuitionAndFees extends React.PureComponent {
  static propTypes = {
    costsData: PropTypes.object,
    tuitionStatistics: PropTypes.object,
    collegeState: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
    };
  }

  getTuitionAndFees = (costsData) => {
    let inStateTuition = Number(get(costsData, 'tuition.inState.value', ''));
    let outOfStateTuition = Number(get(costsData, 'tuition.outOfState.value', ''));
    let tuitionPerCreditHour = Number(
      get(costsData, 'tuition.tuitionPerCreditHour.value', ''),
    );
    let inStateFlatFee = Number(get(costsData, 'inStateFlatFee.value', ''));
    let outOfStateFlatFee = Number(get(costsData, 'outOfStateFlatFee.value', ''));
    let booksFee = Number(get(costsData, 'booksFee.value', ''));
    let roomAndBoardCharge = Number(get(costsData, 'roomAndBoardCharge.value', ''));

    if (Number.isNaN(inStateTuition)) {
      inStateTuition = 0;
    }
    if (Number.isNaN(outOfStateTuition)) {
      outOfStateTuition = 0;
    }
    if (Number.isNaN(inStateFlatFee)) {
      inStateFlatFee = 0;
    }
    if (Number.isNaN(outOfStateFlatFee)) {
      outOfStateFlatFee = 0;
    }
    if (Number.isNaN(booksFee)) {
      booksFee = 0;
    }
    if (Number.isNaN(roomAndBoardCharge) || !roomAndBoardCharge) {
      const roomCharge = Number(get(costsData, 'roomCharge.value', ''));
      const boardCharge = Number(get(costsData, 'boardCharge.value', ''));
      roomAndBoardCharge = 0;
      if (!Number.isNaN(roomCharge)) {
        roomAndBoardCharge = roomAndBoardCharge + roomCharge;
      }
      if (!Number.isNaN(boardCharge)) {
        roomAndBoardCharge = roomAndBoardCharge + boardCharge;
      }
    }
    if (Number.isNaN(tuitionPerCreditHour)) {
      tuitionPerCreditHour = 0;
    }

    return {
      inStateTuition: inStateTuition,
      outOfStateTuition: outOfStateTuition,
      inStateFee: inStateFlatFee + booksFee,
      outOfStateFee: outOfStateFlatFee + booksFee,
      roomAndBoardCharge: roomAndBoardCharge,
      tuitionPerCreditHour: tuitionPerCreditHour,
    };
  };

  getAverageTuitionAndFees = (tuitionStatistics) => {
    const avgInStateTuition = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.inStateTuitionFees.tuition',
      0,
    );
    const avgOutOfStateTuition = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.outOfStateTuitionFees.tuition',
      0,
    );
    const avgInStateFeeWithRoomAndBoard = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.inStateTuitionFees.totalFees',
      0,
    );
    const avgOutOfStateFeeWithRoomAndBoard = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.outOfStateTuitionFees.totalFees',
      0,
    );
    const avgRoomAndBoardCharge = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.roomAndBoardCharge',
      0,
    );
    const avgTuitionPerCreditHour = get(
      tuitionStatistics,
      'FOUR_YEAR.AVERAGES.tuitionPerCreditHour',
      0,
    );

    return {
      avgInStateTuition: avgInStateTuition || 0,
      avgOutOfStateTuition: avgOutOfStateTuition || 0,
      avgInStateFee: avgInStateFeeWithRoomAndBoard
        ? avgInStateFeeWithRoomAndBoard - avgRoomAndBoardCharge
        : 0,
      avgOutOfStateFee: avgOutOfStateFeeWithRoomAndBoard
        ? avgOutOfStateFeeWithRoomAndBoard - avgRoomAndBoardCharge
        : 0,
      avgRoomAndBoardCharge: avgRoomAndBoardCharge || 0,
      avgTuitionPerCreditHour: avgTuitionPerCreditHour || 0,
    };
  };

  getMenuItem = (message, selected = false) => {
    return {
      content: this.props.intl.formatMessage(message),
      key: this.props.intl.formatMessage(message),
      role: 'tab',
      'aria-label': this.props.intl.formatMessage(message),
      'aria-selected': selected,
      className: 'tuition-tab-item',
      tabIndex: 0,
    };
  };

  getPanes() {
    const { activeIndex } = this.state;
    const { costsData, tuitionStatistics, collegeState } = this.props;

    const {
      inStateTuition,
      outOfStateTuition,
      inStateFee,
      outOfStateFee,
      roomAndBoardCharge,
      tuitionPerCreditHour,
    } = this.getTuitionAndFees(costsData);
    const {
      avgInStateTuition,
      avgOutOfStateTuition,
      avgInStateFee,
      avgOutOfStateFee,
      avgRoomAndBoardCharge,
      avgTuitionPerCreditHour,
    } = this.getAverageTuitionAndFees(tuitionStatistics);

    let index = 0;
    const panes = [
      {
        menuItem: this.getMenuItem(messages.totalCost, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={inStateTuition + inStateFee + roomAndBoardCharge}
            outOfStateTuitionAndFees={
              outOfStateTuition + outOfStateFee + roomAndBoardCharge
            }
            avgTuitionAndFees={avgInStateTuition + avgInStateFee + avgRoomAndBoardCharge}
            avgOutOfStateTuitionAndFees={
              avgOutOfStateTuition + avgOutOfStateFee + avgRoomAndBoardCharge
            }
            collegeState={collegeState}
          />
        ),
      },
      {
        menuItem: this.getMenuItem(messages.title, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={inStateTuition + inStateFee}
            outOfStateTuitionAndFees={outOfStateTuition + outOfStateFee}
            avgTuitionAndFees={avgInStateTuition + avgInStateFee}
            avgOutOfStateTuitionAndFees={avgOutOfStateTuition + avgOutOfStateFee}
            collegeState={collegeState}
          />
        ),
      },
      {
        menuItem: this.getMenuItem(messages.tuition, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={inStateTuition}
            outOfStateTuitionAndFees={outOfStateTuition}
            avgTuitionAndFees={avgInStateTuition}
            avgOutOfStateTuitionAndFees={avgOutOfStateTuition}
            collegeState={collegeState}
          />
        ),
      },
      {
        menuItem: this.getMenuItem(messages.fees, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={inStateFee}
            outOfStateTuitionAndFees={outOfStateFee}
            avgTuitionAndFees={avgInStateFee}
            avgOutOfStateTuitionAndFees={avgOutOfStateFee}
            collegeState={collegeState}
          />
        ),
      },
      {
        menuItem: this.getMenuItem(messages.roomAndBoard, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={roomAndBoardCharge}
            avgTuitionAndFees={avgRoomAndBoardCharge}
            collegeState={collegeState}
            isNational
          />
        ),
      },
      {
        menuItem: this.getMenuItem(messages.creditHourTuition, activeIndex === index++),
        render: () => (
          <TuitionAndFeesChart
            tuitionAndFees={tuitionPerCreditHour}
            avgTuitionAndFees={avgTuitionPerCreditHour}
            collegeState={collegeState}
            isNational
          />
        ),
      },
    ];

    return panes;
  }

  handleTabChange = (e, { activeIndex }) => {
    this.setState({
      activeIndex,
    });
  };

  render() {
    const { costsData } = this.props;
    const feeYear = get(costsData, 'tuition.feeYear.value', '');
    const currentYear = new Date().getFullYear();
    if (!feeYear || Number.isNaN(Number(feeYear)) || Number(feeYear) <= currentYear - 3) {
      return null;
    }

    return (
      <div
        name="tuitionandfees"
        data-cy="tuition-and-fees-container"
        className="common-full-width tuition-and-fees-container"
      >
        <div className="common-core-width">
          <h3 className="title-text">
            <FormattedMessage {...messages.title} /> {Number(feeYear)} -{' '}
            {Number(feeYear) + 1}
          </h3>
          <h5 className="title-subtext">
            <FormattedMessage {...messages.subText} />
          </h5>
          <div className="tuition-fees-tabs-container">
            <Tab
              className="tuition-fees-tabs"
              data-cy="tuition-fees-tabs"
              menu={{
                vertical: true,
                tabular: true,
                role: 'tablist',
              }}
              panes={this.getPanes()}
              onTabChange={this.handleTabChange}
              activeIndex={this.state.activeIndex}
            />
          </div>
          <div className="tuition-fees-footer">{`*4 Year schools that reported tuition & fee data to the U.S. Department of Education.`}</div>
        </div>
      </div>
    );
  }
}

export default injectIntl(TuitionAndFees);
